import React, { useState } from "react";
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { PATTERNS } from "../../common/Constants";
import { preventDefault } from "../../../utils/common";
import { navigate } from "raviger";
import { Alert, Button } from "react-bootstrap";
import { getUserDetails, getAuthToken, useChangePassword, clearUserSession } from "../../../backend/auth";
import PageLoader from "../../shared/PageLoader";
import { API_RESULT, ERROR_MESSAGES } from "../../../utils/constants";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import { getBasePath } from "../../../App";

function ChangePasswordModal({ onClose }: any) {
    const [pwdChanged, setPwdChanged] = useState(false);
    const [user] = useState(getUserDetails());
    const [apiError, setApiError] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState<any>({});
    const [passwordError, setPasswordError] = useState<string>('');
    const { changePassword } = useChangePassword(({ result, message }: any) => {
        setSubmitting(false);
        if (result === API_RESULT.SUCCESS) {
            setPwdChanged(true);
        } else {
            setApiError(message);
        }
    }, () => {
        setSubmitting(false);
        setApiError(ERROR_MESSAGES.DEFAULT);
    });

    function signIn(event: any) {
        preventDefault(event);
        clearUserSession();
        navigate(`${getBasePath()}/login`, { replace: true });
        setTimeout(() => {
            window.location.reload();
        });
    }

    const schema = {
        fields: [
            {
                ...LOGIN_FIELDS.PASSWORD,
                name: 'oldPassword',
                label: 'Old Password'
            },
            {
                ...LOGIN_FIELDS.NEW_PASSWORD,
                validate: [
                    { type: validatorTypes.REQUIRED },
                    { type: validatorTypes.PATTERN, pattern: PATTERNS.PASSWORD, message: 'Invalid New Password' },
                    { type: validatorTypes.MIN_LENGTH, threshold: 12 },
                    { type: validatorTypes.MAX_LENGTH, threshold: 128 },
                    (value: any, { oldPassword }: any) => {
                        return value && value === oldPassword ? 'New password cannot be same as old password' : undefined
                    }
                ]
            },
            { ...LOGIN_FIELDS.CONFIRM_PASSWORD }
        ]
    };

    function debugForm(_form: any) {
        setForm(_form);
        const { newPassword, confirmPassword } = _form.values || {};
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            setPasswordError('New password and Re-enter password should be same');
        } else {
            setPasswordError('');
        }
    }

    function isInvalid() {
        return !form.valid || !!passwordError;
    }

    function handleSubmit(event: any) {
        preventDefault(event);
        const { oldPassword, newPassword } = form.values || {};

        if (isInvalid()) {
            if (passwordError) {
                setApiError(passwordError);
            } else {
                setApiError("Please fill all the mandatory fields.");
            }
            return;
        }

        setApiError(null);
        setSubmitting(true);
        changePassword({ username: user.username, oldPassword, newPassword, token: getAuthToken() });
    }

    return (
        <>
            <Modal show={true} backdrop="static" animation={true} dialogClassName="alert-modal" size="lg" centered={true}>
                <Modal.Header closeButton={!pwdChanged} onHide={onClose}>
                    <Modal.Title className="bg">Change Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column align-items-center">
                        {
                            pwdChanged ?
                                <>
                                    <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: '106px' }} className="mb-4 text-success" />
                                    <div className="fs-5 text-success">Password Changed successfully</div>
                                    <p className="pt-5 mt-5 text-black-600">Please log in with the new password.</p>
                                    <div className="text-center pt-4">
                                        <Button variant="primary" type="submit" className={`btn btn-primary px-4`} onClick={signIn}>
                                            Back To LOGIN
                                        </Button>
                                    </div>
                                </> :
                                <>
                                    <div className="col-md-10 m-auto">
                                        <div className="pt-4 pb-4">
                                            <span className="fs-6 text-black-600"> Password Requirements </span>
                                            <div><small className="text-error text-sm">Password must be at least 12 characters long and must include at least one lowercase letter, one uppercase letter, one digit and one special character. The first character of the password cannot be a special character.</small></div>
                                            {
                                                !!apiError && <Alert variant="danger">{apiError}</Alert>
                                            }
                                        </div>
                                        <div className="col-md-9 mx-auto">
                                            <FormRenderer FormTemplate={FormTemplate}
                                                initialValues={{ hideButtons: true }}
                                                componentMapper={ComponentMapper}
                                                schema={schema}
                                                debug={debugForm}
                                            />
                                            {passwordError && (
                                                <div className="text-error text-center mb-3 mt-2">
                                                    <small>{passwordError}</small>
                                                </div>
                                            )}
                                            <div className="d-flex flex-row mt-4 justify-content-center">
                                                <Button
                                                    variant="primary"
                                                    type="button"
                                                    className="btn btn-primary px-4 w-100"
                                                    disabled={isInvalid()}
                                                    onClick={handleSubmit}
                                                >
                                                    Change Password
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                        }
                    </div>
                </Modal.Body>
            </Modal>
            {submitting && <PageLoader />}
        </>
    )
}

export default ChangePasswordModal;
